import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Table, Input, Empty, Tooltip, message } from "antd";
import {
  SearchOutlined,
  RightOutlined,
  FolderOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { healthsafety, observationService } from "@iso/services";
import { commonHelper } from "@iso/lib/helpers/commonHelper";
import {
  getSafetyActivityHazardLabel,
  getSafetyActivityHazardCategoryLabel,
} from "@iso/constants/hazardTypes";
import {
  healthSafetyTypes,
  HEALTH_SAFETY_CATEGORY,
  getHealthSafetyTypeLabel,
} from "@iso/constants/healthSafetyTypes";
import { getStatusColor, getStatusLabel } from "@iso/constants/hazardStatus";
import { assetStates } from "@iso/constants/assetStates";
import { FOLDER_STYLES, FOLDER_DEFAULT_STYLE } from "./SafetyDocumentsModal";

// ---------------------------------------------------------------------------
// Safety Records — the revamped Health & Safety directory (Figma).
//
// A two-tab, master-detail table:
//   • Safety Records  → left-rail sections Assets / Activities / Hazards /
//                       Observations, each with a coloured count; the table on
//                       the right shows type-specific, colour-coded columns.
//   • Safety Documents → left-rail sections are the Documentation & Training
//                       categories; the table lists the tagged assets/forms
//                       with who/when they were uploaded.
// ---------------------------------------------------------------------------

const GREEN = "#067647";
const GREY = "#8c8c8c";

const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "—");

// Humanise a snake/kebab value ("made_safe_in_progress" → "Made Safe In Progress").
const humanise = (v) =>
  (v || "")
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// Strip the "(email)" suffix some allocated_to values carry.
const nameOnly = (v) => (v || "").toString().replace(/\s*\(.*\)\s*$/, "").trim();

// ---- Colour maps -----------------------------------------------------------
// A coloured pill { fg, bg } for a given key, with a neutral fallback.
const pill = (fg, bg) => ({ fg, bg });
const RED = pill("#cf1322", "#fff1f0");
const ORANGE = pill("#d46b08", "#fff7e6");
const GREEN_PILL = pill("#067647", "#f0faf4");
const BLUE = pill("#1890ff", "#e6f7ff");
const NEUTRAL = pill("#595959", "#f5f5f5");

// Hazard status colour — driven by the canonical vocabulary in
// constants/hazardStatus.js so a status never disagrees with the rest of the
// app (substring matching previously turned not_rectified green, etc.).
const hazardStatusStyle = (status) => pill(getStatusColor(status), "transparent");

// Asset state colour — same principle, from constants/assetStates.js.
const assetStateStyle = (state) => {
  const match = assetStates.find((s) => s.value === (state || "").toLowerCase());
  return match ? pill(match.color, "transparent") : NEUTRAL;
};

const approvalStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "approved") return GREEN_PILL;
  if (s === "rejected") return RED;
  return ORANGE; // pending / none
};

// Documentation & Training category colour — shares the exact per-tag map the
// Safety Documents modal uses, so a category is the same colour everywhere.
const docCategoryStyle = (type) => FOLDER_STYLES[type] || FOLDER_DEFAULT_STYLE;

// Fallback hash colour for free-form values that have no canonical map
// (e.g. hazard categories).
const categoryStyle = (value) => {
  const palette = [BLUE, ORANGE, GREEN_PILL, pill("#722ed1", "#f9f0ff"), pill("#eb2f96", "#fff0f6"), pill("#13c2c2", "#e6fffb")];
  let h = 0;
  for (let i = 0; i < (value || "").length; i++) h = (h + value.charCodeAt(i)) % palette.length;
  return palette[h % palette.length];
};

// Due-date colour + label ("Overdue" / "Due in N days").
const dueDateCell = (date) => {
  if (!date) return <span style={{ color: GREY }}>—</span>;
  const due = moment(date).startOf("day");
  const days = due.diff(moment().startOf("day"), "days");
  let fg = GREY;
  let note = null;
  if (days < 0) {
    fg = "#cf1322";
    note = "Overdue";
  } else if (days <= 3) {
    fg = "#d46b08";
    note = days === 0 ? "Due today" : `Due in ${days} day${days === 1 ? "" : "s"}`;
  }
  return (
    <div>
      <div>{due.format("DD MMM YYYY")}</div>
      {note ? <div style={{ color: fg, fontSize: 12, fontWeight: 600 }}>{note}</div> : null}
    </div>
  );
};

const Badge = ({ style, children }) => (
  <span style={{ background: style.bg, color: style.fg, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
    {children}
  </span>
);

const StatusDot = ({ style, children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: style.fg, fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
    <span style={{ width: 8, height: 8, borderRadius: 4, background: style.fg, display: "inline-block" }} />
    {children}
  </span>
);

const TITLE_CELL = (title, sub) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontWeight: 600, color: "#262626", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title || "—"}</div>
    {sub ? <div style={{ color: GREY, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div> : null}
  </div>
);

// Documentation & Training category values, derived from the canonical
// constant so a server-side add/rename flows through without editing this file.
const DOC_TAGS = healthSafetyTypes
  .filter((t) => t.category === HEALTH_SAFETY_CATEGORY.DOCUMENTATION_TRAINING)
  .map((t) => t.value);

const SafetyRecords = ({
  propertyId,
  healthAndSafetyAssets,
  healthAndSafetyPolicies,
  onEditAsset,
  onEditActivity,
  onEditHazard,
  goTo,
}) => {
  const corporateGroupId = useMemo(() => {
    const active = commonHelper.getActiveProperty();
    if (!active || active.id !== propertyId) return null;
    return active.corporate_group_id || null;
  }, [propertyId]);

  const [tab, setTab] = useState("records"); // 'records' | 'documents'
  const [section, setSection] = useState("assets");
  const [search, setSearch] = useState("");
  // Debounced copy of `search`; drives the server-side Documents query so it
  // searches the whole category, not just the loaded page.
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [observations, setObservations] = useState([]);
  const [docCategories, setDocCategories] = useState([]); // [{type,name,count}]
  const [docAssets, setDocAssets] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [page, setPage] = useState(1); // controlled so it resets when rows change

  // Request-id guards so out-of-order responses (fast tab/section switching)
  // can't render a stale list.
  const catReqRef = useRef(0);
  const docReqRef = useRef(0);

  const assets = useMemo(() => (Array.isArray(healthAndSafetyAssets) ? healthAndSafetyAssets : []), [healthAndSafetyAssets]);
  const policies = useMemo(() => (Array.isArray(healthAndSafetyPolicies) ? healthAndSafetyPolicies : []), [healthAndSafetyPolicies]);
  const hazards = useMemo(() => policies.filter((p) => (p.type || "").toLowerCase() === "hazard"), [policies]);
  const activities = useMemo(() => policies.filter((p) => (p.type || "").toLowerCase() !== "hazard"), [policies]);

  // Debounce the search box (used server-side on the Documents tab).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever the row set changes (antd's uncontrolled
  // pagination would otherwise strand the user on an out-of-range page).
  useEffect(() => { setPage(1); }, [tab, section, search]);

  // Observations for the Records tab. Two failure modes both need surfacing:
  // axios rejects on a non-2xx (the .catch), and the service resolves to
  // undefined on HTTP 200 with a non-0000 code.
  useEffect(() => {
    if (!propertyId) return;
    const fail = () => { setObservations([]); message.error("Unable to load observations right now."); };
    observationService
      .getList(propertyId)
      .then((res) => {
        if (res && res.code === "0000") { setObservations(res.observations || []); return; }
        fail();
      })
      .catch(fail);
  }, [propertyId]);

  // Documentation & Training categories (with counts) for the Documents tab.
  useEffect(() => {
    if (tab !== "documents" || !propertyId) return;
    const reqId = ++catReqRef.current;
    healthsafety
      .getSafetyDocuments(propertyId, corporateGroupId)
      .then((res) => { if (reqId === catReqRef.current) setDocCategories((res && res.categories) || []); })
      .catch(() => {
        if (reqId !== catReqRef.current) return;
        setDocCategories([]);
        message.error("Couldn't load safety document categories.");
      });
  }, [tab, propertyId, corporateGroupId]);

  // Assets/forms for the selected Documentation category (server-side search).
  useEffect(() => {
    if (tab !== "documents" || !section || !propertyId) return;
    const reqId = ++docReqRef.current;
    setDocLoading(true);
    healthsafety
      .getSafetyDocumentAssets(section, propertyId, corporateGroupId, 1, 50, debouncedSearch || undefined)
      .then((res) => { if (reqId === docReqRef.current) setDocAssets((res && res.assets) || []); })
      .catch(() => {
        if (reqId !== docReqRef.current) return;
        setDocAssets([]);
        message.error("Couldn't load safety documents.");
      })
      .finally(() => { if (reqId === docReqRef.current) setDocLoading(false); });
  }, [tab, section, propertyId, corporateGroupId, debouncedSearch]);

  const switchTab = useCallback((next) => {
    setTab(next);
    setSearch("");
    setDebouncedSearch("");
    setDocAssets([]); // don't show the old tab's documents under the new heading
    setSection(next === "records" ? "assets" : DOC_TAGS[0]);
  }, []);

  // Rail selection: clear the search so a query from the previous section
  // doesn't silently filter the next one, and drop the previous category's
  // documents so they don't render under the new heading while loading.
  const selectSection = useCallback((key) => {
    setSection(key);
    setSearch("");
    setDebouncedSearch("");
    setDocAssets([]);
  }, []);

  // ---- Section definitions (left rail) -------------------------------------
  const recordSections = [
    { key: "assets", label: "Assets", icon: <DatabaseOutlined />, count: assets.length, style: BLUE },
    { key: "activities", label: "Activities", icon: <ThunderboltOutlined />, count: activities.length, style: pill("#722ed1", "#f9f0ff") },
    { key: "hazards", label: "Hazards", icon: <WarningOutlined />, count: hazards.length, style: ORANGE },
    { key: "observations", label: "Observations", icon: <EyeOutlined />, count: observations.length, style: GREEN_PILL },
  ];

  const docCountMap = useMemo(() => {
    const m = {};
    docCategories.forEach((c) => { m[c.type] = c.count; });
    return m;
  }, [docCategories]);

  const documentSections = DOC_TAGS.map((type) => {
    const cat = docCategories.find((c) => c.type === type);
    return { key: type, label: cat ? cat.name : getHealthSafetyTypeLabel(type), icon: <FolderOutlined />, count: docCountMap[type] || 0, style: docCategoryStyle(type) };
  });

  const sections = tab === "records" ? recordSections : documentSections;
  const activeSection = sections.find((s) => s.key === section) || sections[0];
  const activeLabel = activeSection?.label;

  // ---- Columns per section -------------------------------------------------
  const columns = useMemo(() => {
    const actionCol = (onEdit) => ({
      title: "",
      key: "actions",
      width: 44,
      align: "right",
      render: (_, row) => (
        <Tooltip title="View / edit">
          <button
            type="button"
            aria-label="View or edit"
            onClick={(e) => { e.stopPropagation(); onEdit && onEdit(row); }}
            style={{ border: "none", background: "transparent", cursor: "pointer", color: "#bfbfbf", padding: 4, lineHeight: 1 }}
          >
            <RightOutlined />
          </button>
        </Tooltip>
      ),
    });

    if (tab === "records" && section === "assets") {
      return [
        { title: "Asset", key: "asset", render: (_, r) => TITLE_CELL(r.title, r.description) },
        { title: "Type", key: "type", render: (_, r) => (r.type_label ? <Badge style={NEUTRAL}>{r.type_label}</Badge> : "—") },
        { title: "Location", key: "loc", render: (_, r) => r.storage_location || r.other_storage_location || "—" },
        { title: "State", key: "state", render: (_, r) => (r.state_label ? <Badge style={assetStateStyle(r.state)}>{r.state_label}</Badge> : <Badge style={NEUTRAL}>Unknown</Badge>) },
        { title: "Last Checked", key: "checked", render: (_, r) => fmtDate(r.last_checked_date) },
        actionCol(onEditAsset),
      ];
    }
    if (tab === "records" && section === "activities") {
      return [
        { title: "Activity", key: "activity", render: (_, r) => TITLE_CELL(r.metadata?.meeting_name || humanise(r.type), r.comments) },
        { title: "Type", key: "type", render: (_, r) => <Badge style={NEUTRAL}>{humanise(r.type)}</Badge> },
        { title: "Date", key: "date", render: (_, r) => fmtDate(r.date_time) },
        { title: "Logged By", key: "by", render: (_, r) => r.created_by_name || "—" },
        { title: "Status", key: "status", render: (_, r) => <StatusDot style={approvalStyle(r.latest_approval?.status)}>{humanise(r.latest_approval?.status) || "Pending"}</StatusDot> },
        actionCol(onEditActivity),
      ];
    }
    if (tab === "records" && section === "hazards") {
      return [
        { title: "Hazard", key: "hazard", render: (_, r) => TITLE_CELL(getSafetyActivityHazardLabel(r.metadata?.hazard_type) || humanise(r.metadata?.hazard_type) || "Hazard", r.comments) },
        { title: "Category", key: "cat", render: (_, r) => { const c = r.metadata?.hazard_category; return c ? <Badge style={categoryStyle(c)}>{getSafetyActivityHazardCategoryLabel(c) || humanise(c)}</Badge> : "—"; } },
        { title: "Status", key: "status", render: (_, r) => <StatusDot style={hazardStatusStyle(r.metadata?.hazard_status)}>{r.metadata?.hazard_status ? getStatusLabel(r.metadata.hazard_status) : "Open"}</StatusDot> },
        { title: "Allocated To", key: "owner", render: (_, r) => nameOnly(r.metadata?.allocated_to) || "Unassigned" },
        { title: "Reported", key: "reported", render: (_, r) => fmtDate(r.metadata?.date_time || r.date_time) },
        { title: "Due Date", key: "due", render: (_, r) => dueDateCell(r.metadata?.rectification_due_date) },
        actionCol(onEditHazard),
      ];
    }
    if (tab === "records" && section === "observations") {
      return [
        { title: "Observation", key: "obs", render: (_, r) => TITLE_CELL(r.title, r.comment) },
        { title: "Attachments", key: "att", render: (_, r) => `${(r.images || []).length} file${(r.images || []).length === 1 ? "" : "s"}` },
        { title: "Date", key: "date", render: (_, r) => fmtDate(r.created_at) },
        { title: "Status", key: "status", render: (_, r) => <StatusDot style={approvalStyle(r.latest_approval?.status)}>{humanise(r.latest_approval?.status) || "Pending"}</StatusDot> },
        { title: "Expires", key: "exp", render: (_, r) => dueDateCell(r.expires_at) },
      ];
    }
    // Documents tab
    return [
      { title: "Document", key: "doc", render: (_, r) => TITLE_CELL(r.title, activeLabel) },
      { title: "Type", key: "kind", render: (_, r) => <Badge style={r.kind === "form" ? BLUE : GREEN_PILL}>{r.kind === "form" ? "Form" : "Asset"}</Badge> },
      { title: "Files", key: "files", render: (_, r) => { const n = r.attachment_count || 0; return `${n} file${n === 1 ? "" : "s"}`; } },
      { title: "Uploaded By", key: "by", render: (_, r) => { const u = (r.attachments || [])[0]?.user; return u ? `${u.name || ""} ${u.surname || ""}`.trim() || "—" : "—"; } },
      { title: "Date Uploaded", key: "when", render: (_, r) => fmtDate(r.created_at) },
    ];
  }, [tab, section, activeLabel, onEditAsset, onEditActivity, onEditHazard]);

  // Searchable text for a Records-tab row — only the fields the user can see,
  // so search never matches hidden ids/timestamps/metadata keys.
  const recordText = (r) => {
    if (section === "assets") {
      return [r.title, r.description, r.type_label, r.storage_location, r.other_storage_location, r.state_label];
    }
    if (section === "activities") {
      return [r.metadata?.meeting_name, humanise(r.type), r.comments, r.created_by_name, r.latest_approval?.status];
    }
    if (section === "hazards") {
      const m = r.metadata || {};
      return [
        getSafetyActivityHazardLabel(m.hazard_type), humanise(m.hazard_type),
        getSafetyActivityHazardCategoryLabel(m.hazard_category), humanise(m.hazard_category),
        m.hazard_status ? getStatusLabel(m.hazard_status) : "", nameOnly(m.allocated_to), r.comments,
      ];
    }
    return [r.title, r.comment]; // observations
  };

  // ---- Rows for the active section (with search) ---------------------------
  // Documents are searched server-side (covers the whole category); Records
  // are filtered client-side over their visible fields.
  const rows = useMemo(() => {
    if (tab === "documents") return docAssets;

    let data;
    if (section === "assets") data = assets;
    else if (section === "activities") data = activities;
    else if (section === "hazards") data = hazards;
    else data = observations;

    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) => recordText(r).filter(Boolean).join(" ").toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, section, docAssets, assets, activities, hazards, observations, search]);

  const rowEdit = (row) => {
    if (tab === "documents") { goTo && goTo("/safety-documents", "safety-documents"); return; }
    if (section === "assets") onEditAsset && onEditAsset(row);
    else if (section === "activities") onEditActivity && onEditActivity(row);
    else if (section === "hazards") onEditHazard && onEditHazard(row);
  };

  // Only Hazards and Documents have a dedicated destination page; the other
  // sections have none, so we hide the link rather than link back to self.
  const viewAllTarget =
    tab === "documents"
      ? { path: "/safety-documents", key: "safety-documents" }
      : section === "hazards"
      ? { path: "/hazards", key: "hazards" }
      : null;

  const tabBtn = (key, label) => (
    <button
      type="button"
      onClick={() => switchTab(key)}
      style={{
        border: "none",
        cursor: "pointer",
        padding: "6px 16px",
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 13,
        background: tab === key ? GREEN : "transparent",
        color: tab === key ? "#fff" : "#595959",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ background: "#fff", border: "1px solid #f0f0f0", borderRadius: 12, overflow: "hidden" }}>
      <style>{`
        .safety-records-table .ant-table-thead > tr > th { background:#fafafa; color:#8c8c8c; font-weight:600; font-size:12px; text-transform:uppercase; letter-spacing:.3px; }
        .safety-records-table .ant-table-tbody > tr { cursor:pointer; }
        .safety-records-table .ant-table-tbody > tr:hover > td { background:rgba(6,118,71,.04); }
        .sr-rail-item:hover { background:#f7f8fa; }
      `}</style>

      {/* Header: tabs + search */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Safety Records</span>
          <div style={{ display: "inline-flex", background: "#f0f2f5", borderRadius: 10, padding: 3, gap: 2 }}>
            {tabBtn("records", "Safety Records")}
            {tabBtn("documents", "Safety Documents")}
          </div>
        </div>
        <Input
          allowClear
          placeholder="Search records…"
          prefix={<SearchOutlined style={{ color: "#8F92A1" }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280, maxWidth: "50vw", borderRadius: 8 }}
        />
      </div>

      {/* Body: rail + table */}
      <div style={{ display: "flex", minHeight: 320 }}>
        {/* Left rail */}
        <div style={{ width: 232, borderRight: "1px solid #f0f0f0", padding: 12, flexShrink: 0, overflowY: "auto", maxHeight: 520 }}>
          {tab === "documents" && (
            <div style={{ fontSize: 11, color: "#bfbfbf", textTransform: "uppercase", letterSpacing: 0.4, margin: "4px 8px 8px" }}>Categories</div>
          )}
          {sections.map((s) => {
            const active = s.key === section;
            return (
              <div
                key={s.key}
                className="sr-rail-item"
                role="button"
                tabIndex={0}
                aria-pressed={active}
                onClick={() => selectSection(s.key)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectSection(s.key); } }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, cursor: "pointer", marginBottom: 2,
                  background: active ? "rgba(6,118,71,.08)" : "transparent",
                  color: active ? GREEN : "#262626", fontWeight: active ? 700 : 500,
                }}
              >
                <span style={{ color: active ? GREEN : "#8c8c8c", display: "inline-flex" }}>{s.icon}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>{s.label}</span>
                <span style={{ background: s.style.bg, color: s.style.fg, borderRadius: 20, padding: "1px 9px", fontSize: 12, fontWeight: 700, minWidth: 24, textAlign: "center" }}>{s.count}</span>
              </div>
            );
          })}
        </div>

        {/* Main table */}
        <div style={{ flex: 1, minWidth: 0, padding: 8 }}>
          <Table
            className="safety-records-table"
            columns={columns}
            dataSource={rows}
            rowKey={(r) => `${tab}-${r.kind || section}-${r.id}`}
            size="middle"
            loading={tab === "documents" && docLoading}
            pagination={rows.length > 8 ? { current: page, pageSize: 8, size: "small", showSizeChanger: false, onChange: setPage } : false}
            onRow={(row) => ({ onClick: () => rowEdit(row) })}
            locale={{ emptyText: <Empty description={`No ${activeSection?.label?.toLowerCase() || "records"} yet`} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
          {tab === "documents" && !docLoading && !debouncedSearch && (docCountMap[section] || 0) > docAssets.length && (
            <div style={{ padding: "4px 8px", color: GREY, fontSize: 12 }}>
              Showing the first {docAssets.length} of {docCountMap[section]} — search or “View all” to find the rest.
            </div>
          )}
          {viewAllTarget && (
            <div style={{ padding: "6px 8px 4px" }}>
              <button
                type="button"
                onClick={() => goTo && goTo(viewAllTarget.path, viewAllTarget.key)}
                style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: GREEN, fontWeight: 600, fontSize: 13 }}
              >
                View all {(activeLabel || "").toLowerCase()} <RightOutlined style={{ fontSize: 10 }} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SafetyRecords;
